import type { ApolloQueryResult } from '@apollo/client/core';

import { readonly, shallowRef } from 'vue';

import {
  beginDataRefreshFeedbackScope,
  getActiveQueriesApolloRuntimeClient,
  refetchActiveApolloQueries,
  type ActiveQueriesApolloRuntimeClient,
} from '@/shared/api';

import type {
  DataRefreshCoordinator,
  DataRefreshCoordinatorOptions,
  DataRefreshFailureKind,
  DataRefreshOutcome,
  DataRefreshTrigger,
} from './types';

const getExceptionClass = (error: unknown): string | null => {
  if (error instanceof Error) {
    return error.name || 'Error';
  }

  return error === null || error === undefined ? null : typeof error;
};

const hasGraphQLErrors = (results: readonly ApolloQueryResult<unknown>[]): boolean => {
  return results.some((result) => Boolean(result.errors?.length || result.error?.graphQLErrors.length));
};

const resolveFailureKind = ({
  hasBatchGraphQLErrors,
  graphQLErrorOperationCount,
  networkErrorOperationCount,
  exception,
}: {
  hasBatchGraphQLErrors: boolean;
  graphQLErrorOperationCount: number;
  networkErrorOperationCount: number;
  exception: unknown;
}): DataRefreshFailureKind => {
  if (networkErrorOperationCount > 0) {
    return 'network';
  }

  if (hasBatchGraphQLErrors || graphQLErrorOperationCount > 0) {
    return 'graphql';
  }

  if (exception && typeof exception === 'object' && 'networkError' in exception) {
    return 'network';
  }

  return 'unknown';
};

const defaultWarn = (message: string, context: Parameters<NonNullable<DataRefreshCoordinatorOptions['warn']>>[1]) => {
  console.warn(message, context);
};

export const createDataRefreshCoordinator = (
  client: ActiveQueriesApolloRuntimeClient = getActiveQueriesApolloRuntimeClient(),
  options: DataRefreshCoordinatorOptions = {},
): DataRefreshCoordinator => {
  const status = shallowRef<'idle' | 'refreshing'>('idle');
  const isOnline = options.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
  const canRefresh = options.canRefresh ?? (() => true);
  const warn = options.warn ?? defaultWarn;
  let isDisposed = false;
  let inFlight: Promise<DataRefreshOutcome> | null = null;

  const runRefresh = async (trigger: DataRefreshTrigger): Promise<DataRefreshOutcome> => {
    const feedbackScope = beginDataRefreshFeedbackScope();
    let queryCount = 0;
    let results: readonly ApolloQueryResult<unknown>[] = [];
    let exception: unknown = null;

    status.value = 'refreshing';

    try {
      queryCount = client.getObservableQueries('active').size;
      const refetch = () => refetchActiveApolloQueries(client);
      const batch = await (options.runExclusive ? options.runExclusive(refetch) : refetch());
      queryCount = batch.queryCount;
      results = batch.results;
    } catch (error: unknown) {
      exception = error;
    } finally {
      const feedback = feedbackScope.close();
      const hasBatchGraphQLErrors = hasGraphQLErrors(results);
      const hasFailure =
        exception !== null ||
        hasBatchGraphQLErrors ||
        feedback.graphQLErrorOperationCount > 0 ||
        feedback.networkErrorOperationCount > 0;

      if (hasFailure) {
        const failureKind = resolveFailureKind({
          hasBatchGraphQLErrors,
          graphQLErrorOperationCount: feedback.graphQLErrorOperationCount,
          networkErrorOperationCount: feedback.networkErrorOperationCount,
          exception,
        });
        const outcome: DataRefreshOutcome = {
          kind: 'failed',
          trigger,
          queryCount,
          failureKind,
        };

        warn('[data-refresh] Active query refresh failed', {
          kind: failureKind,
          trigger,
          queryCount,
          exceptionClass: getExceptionClass(exception),
        });

        if (trigger === 'manual') {
          options.notifyManualIssue?.(outcome);
        }

        return outcome;
      }
    }

    return {
      kind: 'success',
      trigger,
      queryCount,
    };
  };

  const refresh = (trigger: DataRefreshTrigger): Promise<DataRefreshOutcome> => {
    if (inFlight) {
      return inFlight;
    }

    if (isDisposed || !canRefresh()) {
      return Promise.resolve({ kind: 'skipped', trigger, queryCount: 0 });
    }

    if (!isOnline()) {
      const outcome: DataRefreshOutcome = { kind: 'offline', trigger, queryCount: 0 };
      if (trigger === 'manual') {
        options.notifyManualIssue?.(outcome);
      }
      return Promise.resolve(outcome);
    }

    const promise = runRefresh(trigger).finally(() => {
      status.value = 'idle';
      if (inFlight === promise) {
        inFlight = null;
      }
    });
    inFlight = promise;

    return promise;
  };

  return {
    status: readonly(status),
    refresh,
    dispose: () => {
      isDisposed = true;
    },
  };
};
